import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp,
  Calendar,
  Play,
  MessageSquare,
  Target,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackReport() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSession();
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const sessionRes = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(sessionRes.data);
      
      const scenarioRes = await axios.get(`${API}/scenarios/${sessionRes.data.scenario_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScenario(scenarioRes.data);
    } catch (error) {
      toast.error('Failed to load feedback');
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreRingColor = (score) => {
    if (score >= 80) return '#00E085';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00E085]"></div>
      </div>
    );
  }

  if (!session || !scenario) return null;

  const scores = session.scores || { confidence: 0, clarity: 0, tone: 0, effectiveness: 0 };
  const overallScore = session.overall_score || 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="feedback-report">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/dashboard" className="text-gray-600 hover:text-gray-900" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-semibold text-[#111827]">Session Feedback</h1>
            <p className="text-sm text-gray-500">{scenario.title}</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Overall Score Card */}
        <div className="bg-white rounded-3xl p-8 mb-8 text-center" data-testid="overall-score-card">
          <div className="relative w-40 h-40 mx-auto mb-6">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="8"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke={getScoreRingColor(overallScore)}
                strokeWidth="8"
                strokeDasharray={`${(overallScore / 100) * 283} 283`}
                className="score-ring"
                style={{ transition: 'stroke-dashoffset 1s ease-out' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-5xl font-bold ${getScoreColor(overallScore)}`}>
                {overallScore}
              </span>
              <span className="text-gray-500 text-sm">out of 100</span>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-[#111827] mb-2">
            {overallScore >= 80 ? 'Great job!' : overallScore >= 60 ? 'Good effort!' : 'Keep practicing!'}
          </h2>
          <p className="text-gray-600">{session.feedback_summary}</p>
        </div>

        {/* Category Scores */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries(scores).map(([category, score]) => (
            <div key={category} className="bg-white rounded-2xl p-5" data-testid={`score-${category}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-500 capitalize">{category}</span>
                <span className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-1000"
                  style={{ 
                    width: `${score}%`,
                    backgroundColor: getScoreRingColor(score)
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Highlight & Tips */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* You Did Well */}
          <div className="bg-[#00E085]/10 rounded-2xl p-6" data-testid="highlight-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#00E085] rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-[#111827]">You Did Well</h3>
            </div>
            <p className="text-gray-700">
              {session.signals?.highlight || 'Good effort engaging with the conversation partner.'}
            </p>
          </div>

          {/* Improve Next */}
          <div className="bg-[#6366F1]/10 rounded-2xl p-6" data-testid="tips-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#6366F1] rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-[#111827]">Improve Next Time</h3>
            </div>
            <ul className="space-y-3">
              {(session.coaching_tips || ['Try using more "I" statements', 'State your goal earlier in the conversation', 'End with a clear next step']).map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="w-5 h-5 bg-[#6366F1]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-[#6366F1]">{i + 1}</span>
                  </span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* YOUR NEXT STEPS - Resolution Engine */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-8" data-testid="next-steps-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#111827]">Your Next Steps</h3>
              <p className="text-sm text-gray-600">Clear actions to continue improving</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-2">Immediate Action</p>
              <p className="text-gray-800 font-medium">
                {session.next_steps?.immediate || 'Practice stating your needs clearly at the start'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-2">Skill to Develop</p>
              <p className="text-gray-800 font-medium">
                {session.next_steps?.skill_focus || 'Active listening and acknowledgment'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-2">Suggested Next</p>
              <p className="text-gray-800 font-medium">
                {session.next_steps?.scenario_suggestion || 'Try a similar scenario at higher difficulty'}
              </p>
            </div>
          </div>
          
          {/* Resolution Status */}
          <div className="mt-4 pt-4 border-t border-amber-200 flex items-center gap-2">
            {session.signals?.reached_resolution ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700 font-medium">You reached a resolution in this conversation!</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-amber-700">Focus on guiding your next conversation toward a clear resolution</span>
              </>
            )}
          </div>
        </div>

        {/* Session Stats */}
        <div className="bg-white rounded-2xl p-6 mb-8">
          <h3 className="font-semibold text-[#111827] mb-4">Session Details</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-[#111827]">
                {Math.floor((session.duration_seconds || 0) / 60)}:{String((session.duration_seconds || 0) % 60).padStart(2, '0')}
              </p>
              <p className="text-sm text-gray-500">Duration</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#111827]">
                {session.difficulty}
              </p>
              <p className="text-sm text-gray-500">Difficulty</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#111827]">
                {session.signals?.directness || 'medium'}
              </p>
              <p className="text-sm text-gray-500">Directness</p>
            </div>
          </div>
        </div>

        {/* Educational Disclaimer */}
        <div className="rounded-2xl p-5 mb-8 text-center" style={{ background: '#FAF8F5', border: '1px solid rgba(0,0,0,0.06)' }} data-testid="session-disclaimer">
          <p className="text-xs leading-relaxed" style={{ color: '#8B7355', fontFamily: "'DM Sans', sans-serif" }}>
            This summary is provided for educational purposes only and should not be considered financial, investment, legal, or tax advice. Consult qualified professionals before making financial decisions.
          </p>
        </div>

        {/* CTAs */}
        <div className="bg-gradient-to-r from-[#111827] to-[#1F2937] rounded-2xl p-8 text-center">
          <Sparkles className="w-10 h-10 text-[#00E085] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Ready to Practice Again?</h3>
          <p className="text-gray-400 mb-6">Apply what you learned and aim for resolution</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/scenarios">
              <Button className="bg-[#00E085] text-[#00331E] hover:bg-[#00C978] rounded-full px-6" data-testid="practice-again-btn">
                <Play className="w-4 h-4 mr-2" />
                Practice Again
              </Button>
            </Link>
          </div>
        </div>

        {/* Prosperitas Financial Lead-Gen CTA */}
        <div
          className="rounded-2xl overflow-hidden mt-8"
          style={{ background: '#1C3D2E' }}
          data-testid="prosperitas-cta"
        >
          <div className="px-8 py-10 sm:px-12 sm:py-14 flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
            {/* Left — Jerry's photo + personal intro */}
            <div className="flex-shrink-0 flex flex-col items-center lg:items-start">
              <div className="relative mb-4">
                <img
                  src="/jerry-ai.png"
                  alt="Jerry Citarella — Prosperitas Financial"
                  className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-cover"
                  style={{ border: '3px solid rgba(212,168,83,0.4)' }}
                  data-testid="jerry-photo"
                />
                <div
                  className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: '#D4A853' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1C3D2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
              </div>
              <p className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: '#F5EFE6' }}>Jerry Citarella</p>
              <p className="text-[11px] mt-0.5" style={{ color: 'rgba(245,239,230,0.5)' }}>Founder & Financial Advisor</p>
            </div>

            {/* Right — editorial copy + CTA */}
            <div className="flex-1">
              <p
                className="text-xs font-semibold tracking-[0.15em] uppercase mb-3"
                style={{ color: '#C4613A' }}
              >
                Prosperitas Financial
              </p>
              <h3
                className="text-2xl sm:text-3xl leading-snug font-semibold mb-3"
                style={{ fontFamily: "'Playfair Display', serif", color: '#F5EFE6' }}
              >
                You've practiced the conversation.<br />
                Now have it with a <span style={{ fontStyle: 'italic', color: '#D4A853' }}>real expert.</span>
              </h3>
              <p
                className="text-sm sm:text-base leading-relaxed mb-5 max-w-lg"
                style={{ color: 'rgba(245,239,230,0.6)', fontFamily: "'DM Sans', sans-serif" }}
              >
                AI can help you rehearse — but your finances deserve a human who listens. Jerry and the team at Prosperitas Financial specialize in turning these exact conversations into real outcomes for your family.
              </p>

              {/* Trust signals inline */}
              <div className="flex items-center gap-5 mb-6">
                {[
                  { val: 'CFP', label: 'Certified' },
                  { val: '15+', label: 'Years' },
                  { val: '1:1', label: 'Human' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="text-base font-bold" style={{ fontFamily: "'Playfair Display', serif", color: '#D4A853' }}>{item.val}</span>
                    <span className="text-[11px]" style={{ color: 'rgba(245,239,230,0.4)' }}>{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <a href="https://www.prosperitasfinancial.com" target="_blank" rel="noopener noreferrer">
                  <Button
                    className="rounded-full px-7 py-5 text-sm font-semibold shadow-none"
                    style={{
                      background: '#D4A853',
                      color: '#1C3D2E',
                      fontFamily: "'DM Sans', sans-serif",
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(212,168,83,0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    data-testid="prosperitas-speak-btn"
                  >
                    Speak With Jerry
                  </Button>
                </a>
                <span className="text-xs" style={{ color: 'rgba(245,239,230,0.35)' }}>Free 30-minute consultation</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
